/**
 * @file useTimeFormat
 * Hook that provides time and date formatting utilities based on operator settings.
 */

import { useMemo, useCallback } from "react";
import moment, { Moment } from "moment-timezone";
import { useOperator } from "./useOperator";
import { ClockTypeEnum, DateFormatEnum } from "types";

export type TimeFormatOptions = {
  /** Include date in the output */
  includeDate?: boolean;
  /** Date format to use when includeDate is true */
  dateFormat?: string;
};

export type TimeFormats = {
  /** Time format string (e.g., "h:mm A" or "HH:mm") */
  time: string;
  /** Time format with seconds */
  timeWithSeconds: string;
  /** Date format string (e.g., "MM/DD/YY" or "DD/MM/YY") */
  date: string;
  /** Date format for display (e.g., "MMM D, YYYY") */
  dateDisplay: string;
  /** Format function for time */
  formatTime: (
    date: Moment | Date | string | null | undefined,
    options?: TimeFormatOptions
  ) => string;
  /** Format function for dates */
  formatDate: (
    date: Moment | Date | string | null | undefined,
    useDisplayFormat?: boolean
  ) => string;
  /** Format function for date and time combined */
  formatDateTime: (
    date: Moment | Date | string | null | undefined,
    useDisplayFormat?: boolean
  ) => string;
  /** Get the appropriate format string */
  getTimeFormat: (options?: TimeFormatOptions) => string;
  /** Get the appropriate date format string */
  getDateFormat: (useDisplayFormat?: boolean) => string;
  /** Check if using 24-hour format */
  is24Hour: boolean;
  /** Check if using DD/MM/YYYY format */
  isDayFirst: boolean;
};

export function useTimeFormat(): TimeFormats {
  const { operator } = useOperator();
  const clockType = operator?.settings?.clockType || ClockTypeEnum.TwelveHour;
  const dateFormatSetting = operator?.settings?.dateFormat || DateFormatEnum.MmDdYyyy;

  const formats = useMemo(() => {
    const is24Hour = clockType === ClockTypeEnum.TwentyFourHour;
    const isDayFirst = dateFormatSetting === DateFormatEnum.DdMmYyyy;

    // Base format strings
    const timeFormat = is24Hour ? "HH:mm" : "h:mm A";
    const timeWithSecondsFormat = is24Hour ? "HH:mm:ss" : "h:mm:ss A";
    
    // Date format strings
    const dateFormat = isDayFirst ? "DD/MM/YY" : "MM/DD/YY";
    const dateDisplayFormat = isDayFirst ? "D MMM YYYY" : "MMM D, YYYY";

    return {
      time: timeFormat,
      timeWithSeconds: timeWithSecondsFormat,
      date: dateFormat,
      dateDisplay: dateDisplayFormat,
      is24Hour,
      isDayFirst,
    };
  }, [clockType, dateFormatSetting]);

  const formatTime = useCallback(
    (
      date: Moment | Date | string | null | undefined,
      options: TimeFormatOptions = {}
    ): string => {
      if (!date) {
        return "";
      }

      const momentDate = moment(date);
      const timeFormat = formats.time;

      let formatted: string;

      if (options.includeDate && options.dateFormat) {
        // When including date, append the time format to the date format
        const fullFormat = `${options.dateFormat} ${timeFormat}`;
        formatted = momentDate.format(fullFormat);
      } else {
        formatted = momentDate.format(timeFormat);
      }

      return formatted;
    },
    [formats]
  );

  const formatDate = useCallback(
    (
      date: Moment | Date | string | null | undefined,
      useDisplayFormat: boolean = false
    ): string => {
      if (!date) {
        return "";
      }

      const momentDate = moment(date);
      const dateFormat = useDisplayFormat ? formats.dateDisplay : formats.date;
      
      return momentDate.format(dateFormat);
    },
    [formats]
  );

  const formatDateTime = useCallback(
    (
      date: Moment | Date | string | null | undefined,
      useDisplayFormat: boolean = false
    ): string => {
      if (!date) {
        return "";
      }

      const momentDate = moment(date);
      const dateFormat = useDisplayFormat ? formats.dateDisplay : formats.date;
      const fullFormat = `${dateFormat} ${formats.time}`;
      
      return momentDate.format(fullFormat);
    },
    [formats]
  );

  const getTimeFormat = useCallback((): string => {
    return formats.time;
  }, [formats]);

  const getDateFormat = useCallback((useDisplayFormat: boolean = false): string => {
    return useDisplayFormat ? formats.dateDisplay : formats.date;
  }, [formats]);

  return {
    ...formats,
    formatTime,
    formatDate,
    formatDateTime,
    getTimeFormat,
    getDateFormat,
  };
}
